import { DataOwnerOrStub, IccDataOwnerXApi } from '../icc-data-owner-x-api'
import { KeyPair, ShaVersion } from './RSA'
import { ua2hex } from '../utils'
import { IcureStorageFacade } from '../storage/IcureStorageFacade'
import { fingerprintV1 } from './utils'
import { CryptoPrimitives } from './CryptoPrimitives'
import { KeyRecovery } from './KeyRecovery'
import { CryptoStrategies } from './CryptoStrategies'
import { DataOwnerWithType } from '../../icc-api/model/DataOwnerWithType'
import { CryptoActorStub, CryptoActorStubWithType } from '../../icc-api/model/CryptoActorStub'
import { KeyPairRecoverer } from './KeyPairRecoverer'

type KeyPairData = { pair: KeyPair<CryptoKey>; isVerified: boolean; isDevice: boolean }
type KeyRecovererAndVerifier = (
  keysData: {
    dataOwner: DataOwnerWithType
    unknownKeys: string[]
    unavailableKeys: string[]
  }[]
) => Promise<{
  [dataOwnerId: string]: {
    recoveredKeys: { [keyPairFingerprint: string]: KeyPair<CryptoKey> }
    keyAuthenticity: { [keyPairFingerprint: string]: boolean }
  }
}>
const nothingKeyRecovererAndVerifier: KeyRecovererAndVerifier = (x) =>
  Promise.resolve(
    x.reduce(
      (acc, { dataOwner }) => ({ ...acc, [dataOwner.dataOwner.id!]: { recoveredKeys: {}, keyAuthenticity: {} } }),
      {} as {
        [dataOwnerId: string]: {
          recoveredKeys: { [keyPairFingerprint: string]: KeyPair<CryptoKey> }
          keyAuthenticity: { [keyPairFingerprint: string]: boolean }
        }
      }
    )
  )
type CurrentOwnerKeyGenerator = (self: DataOwnerWithType) => Promise<KeyPair<CryptoKey> | boolean | 'keyless'>

/**
 * Allows to manage public and private keys for the current user and his parent hierarchy.
 */
export class UserEncryptionKeysManager {
  private selfId: string | undefined
  private selfLegacyPublicKey: string | undefined
  private keysCache: { [selfOrParentId: string]: { [pubKeyFingerprint: string]: KeyPairData } } | undefined = undefined

  constructor(
    private readonly primitives: CryptoPrimitives,
    private readonly dataOwnerApi: IccDataOwnerXApi,
    private readonly icureStorage: IcureStorageFacade,
    private readonly keyRecovery: KeyRecovery,
    private readonly strategies: CryptoStrategies,
    private readonly initialiseParentKeys: boolean,
    private readonly keypairRecoverer: KeyPairRecoverer,
    private readonly selfIsAnonymous: boolean
  ) {}

  /**
   * @internal
   * Get all key pairs available for the current data owner and his parents.
   * @return an object with:
   * - `self` an object containing the current data owner id and the list of key pairs available for the current data owner with verification details.
   * - `parents` the list of parents to the current data owner with the list of key pairs available for each parent. The list is ordered from the
   *   topmost ancestor (at index 0) to the direct parent of the current data owner (at the last index, may be 0).
   */
  async getCurrentUserHierarchyAvailableKeypairs(): Promise<{
    self: {
      dataOwnerId: string
      keys: { pair: KeyPair<CryptoKey>; verified: boolean }[]
    }
    parents: {
      dataOwnerId: string
      keys: { pair: KeyPair<CryptoKey>; verified: boolean }[]
    }[]
  }> {
    this.ensureInitialised()
    const hierarchy = await this.dataOwnerApi.getCurrentDataOwnerHierarchyIds()
    const selfId = hierarchy[hierarchy.length - 1]
    const selfKeys = Object.values(this.keysCache![selfId]).map(({ pair, isVerified, isDevice }) => ({ pair, verified: isVerified || isDevice }))
    const remainingHierarchy = hierarchy.slice(0, hierarchy.length - 1)
    return {
      self: {
        dataOwnerId: selfId,
        keys: selfKeys,
      },
      parents: remainingHierarchy.map((x) => ({
        dataOwnerId: x,
        keys: Object.values(this.keysCache![x]).map(({ pair, isVerified, isDevice }) => ({ pair, verified: isVerified || isDevice })),
      })),
    }
  }

  /**
   * Get the public keys of available key pairs for the current user in hex-encoded spki representation (uses cached keys: no request is done to the
   * server).
   * By setting {@link verifiedOnly} to true only the public keys for verified key pairs will be returned: these will include only key pairs created
   * on this device or which have been verified using {@link CryptoStrategies} on this device.
   * @param verifiedOnly if true only the verified public keys will be returned.
   * @return the spki representation of public keys of available keypairs for the current user.
   */
  async getCurrentUserAvailablePublicKeysHex(verifiedOnly: boolean): Promise<string[]> {
    this.ensureInitialised()
    let selectedData = Object.values(this.keysCache![this.selfId!])
    if (verifiedOnly) {
      selectedData = selectedData.filter((data) => data.isVerified || data.isDevice)
    }
    return await Promise.all(selectedData.map(async (keyData) => ua2hex(await this.primitives.RSA.exportKey(keyData.pair.publicKey, 'spki'))))
  }

  /**
   * Get the public keys of available key pairs for the current user and his parents in hex-encoded spki representation (uses cached keys: no request
   * is done to the server).
   * Note that this will also include unverified keys.
   * @return the spki representation of public keys of available keypairs for the current user.
   */
  async getCurrentUserHierarchyAvailablePublicKeysHex(): Promise<string[]> {
    return await Promise.all(
      Object.values(this.keysCache!)
        .flatMap((pairsForParent) => Object.values(pairsForParent))
        .map(async (keyPairData) => ua2hex(await this.primitives.RSA.exportKey(keyPairData.pair.publicKey, 'spki')))
    )
  }

  /**
   * Get a key pair with the provided fingerprint if present.
   * @param fingerprint a key-pair/public-key fingerprint
   * @return the pair associated to the fingerprint and a boolean indicating if the pair is verified, if present, else undefined
   */
  getKeyPairForFingerprint(fingerprint: string): { pair: KeyPair<CryptoKey>; verified: boolean } | undefined {
    const foundVerified = this.getSelfVerifiedKeys().find((x) => x.fingerprint === fingerprint)
    if (foundVerified) return { pair: foundVerified.pair, verified: true }
    const foundOther = this.getDecryptionKeys()[fingerprint]
    if (foundOther) return { pair: foundOther, verified: false }
    return undefined
  }

  /**
   * @internal This method is intended for internal use only and may be changed without notice.
   * Initializes all keys for the current data owner. This method needs to be called before any other method of this class can be used.
   * @throws if the current user is not a data owner, or if there is no key and no new key could be created according to this manager crypt
   * strategies.
   * @return if a new key was created during initialisation the newly created key, else undefined.
   */
  async initialiseKeys(): Promise<{ newKeyPair: KeyPair<CryptoKey>; newKeyFingerprint: string } | undefined> {
    const newKey = await this.doLoadKeys(
      (x) => this.strategies.recoverAndVerifySelfHierarchyKeys(x, this.primitives, this.keypairRecoverer),
      (x) => this.strategies.generateNewKeyForDataOwner(x, this.primitives)
    )
    return newKey ? { newKeyPair: newKey.pair, newKeyFingerprint: newKey.fingerprint } : undefined
  }

  /**
   * @internal This method is intended for internal use only and may be changed without notice.
   * Forces to reload keys for the current data owner. This could be useful if the data owner has logged in from another device in order to update the
   * transfer keys.
   * This method will complete only after keys have been reloaded successfully.
   */
  async reloadKeys(): Promise<void> {
    await this.doLoadKeys(nothingKeyRecovererAndVerifier, (x) => {
      throw new Error("Can't create new keys at reload time: it should have already been created on initialisation")
    })
  }

  /**
   * @internal This method is intended for internal use only and may be changed without notice.
   * Get all verified key pairs for the current data owner which can safely be used for encryption. This includes all key pairs created on the current
   * device and all recovered key pairs which have been verified.
   * The keys returned by this method will be in the following order:
   * 1. Legacy key pair if it is verified
   * 2. All device key pais, in alphabetical order according to the fingerprint
   * 3. Other verified key pairs, in alphabetical order according to the fingerprint
   * @return all verified keys, in order.
   */
  getSelfVerifiedKeys(): { fingerprint: string; pair: KeyPair<CryptoKey> }[] {
    this.ensureInitialised()
    const selfKeys = this.keysCache![this.selfId!]
    const allKeysEntries = Object.entries(selfKeys)

    const legacyKeyFp = this.selfLegacyPublicKey && fingerprintV1(this.selfLegacyPublicKey)
    const legacyKeyData = legacyKeyFp ? selfKeys[legacyKeyFp] : undefined
    const legacyEntry = legacyKeyData?.isVerified && legacyKeyFp ? [{ fingerprint: legacyKeyFp, pair: legacyKeyData.pair }] : []

    function filteredEntries(filterFunction: (fp: string, data: KeyPairData) => boolean) {
      return allKeysEntries
        .filter(([fp, data]) => filterFunction(fp, data) && fp !== legacyKeyFp)
        .sort(([a], [b]) => {
          // need to make sure that the comparison is independent of the locale, but the actual ordering is not that important
          return a == b ? 0 : a > b ? 1 : -1
        })
        .map(([fingerprint, { pair }]) => ({ fingerprint, pair }))
    }
    return [...legacyEntry, ...filteredEntries((_, data) => data.isDevice), ...filteredEntries((_, data) => !data.isDevice && data.isVerified)]
  }

  /**
   * Get all verified keys for a member of the current data owner hierarchy in no particular order.
   * @param dataOwner the current data owner or a member of his hierarchy.
   * @throws if the provided data owner is not part of the current data owner hierarchy
   */
  async getVerifiedPublicKeysFor(dataOwner: DataOwnerOrStub): Promise<string[]> {
    this.ensureInitialised()
    const availableKeys = this.keysCache![dataOwner.id!]
    if (!availableKeys) throw new Error(`Data owner ${dataOwner.id} is not part of the hierarchy of the current data owner ${this.selfId}`)
    const availableVerifiedKeysFp = new Set(Object.entries(availableKeys).flatMap(([fp, info]) => (info.isVerified || info.isDevice ? [fp] : [])))
    const otherVerifiedFp = new Set(
      Object.entries(await this.icureStorage.loadSelfVerifiedKeys(dataOwner.id!)).flatMap(([fp, verified]) => (verified ? [fp] : []))
    )
    return Array.from([
      ...this.dataOwnerApi.getHexPublicKeysWithSha1Of(dataOwner),
      ...this.dataOwnerApi.getHexPublicKeysWithSha256Of(dataOwner),
    ]).filter((key) => {
      const fp = fingerprintV1(key)
      return availableVerifiedKeysFp.has(fp) || otherVerifiedFp.has(fp)
    })
  }

  /**
   * @internal This method is intended for internal use only and may be changed without notice.
   * Get all key pairs for the current data owner and his parents. These keys should be used only for decryption as they may have not been verified.
   * @return all key pairs available for decryption.
   */
  getDecryptionKeys(): { [fingerprint: string]: KeyPair<CryptoKey> } {
    this.ensureInitialised()
    return Object.values(this.keysCache!).reduce((acc, curr) => {
      return {
        ...acc,
        ...this.plainKeysByFingerprint(curr),
      }
    }, {} as { [fp: string]: KeyPair<CryptoKey> })
  }

  private async doLoadKeys(
    keyRecovererAndVerifier: KeyRecovererAndVerifier,
    currentOwnerKeyGenerator: CurrentOwnerKeyGenerator
  ): Promise<{ pair: KeyPair<CryptoKey>; fingerprint: string } | undefined> {
    // Load all keys for self from key store
    const hierarchy = (await this.dataOwnerApi.getCurrentDataOwnerHierarchy()).map(({ dataOwner, type }) => {
      if (dataOwner.publicKey == '') {
        if (Object.entries(dataOwner.hcPartyKeys ?? {}).length > 0)
          throw new Error(`Data owner ${dataOwner.id} has "" as public key but has non-empty hcPartyKeys`)
        delete dataOwner.publicKey
      }
      return {
        dataOwner,
        type,
      } as DataOwnerWithType
    })
    const self = hierarchy[hierarchy.length - 1]
    this.selfId = self.dataOwner.id!
    const keysData = []
    const toInit = this.initialiseParentKeys ? hierarchy : [self]
    for (let i = 0; i < toInit.length; i++) {
      const dowt = toInit[i]
      let parentKeysForRecovery: { [keyFp: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } } = {}
      for (let j = 0; j < i; j++) {
        parentKeysForRecovery = {
          ...keysData[j].availableKeys,
          ...parentKeysForRecovery,
        }
      }
      const availableKeys = await this.loadAndRecoverKeysFor(dowt, parentKeysForRecovery)
      const availableKeysFpSet = new Set(Object.keys(availableKeys))
      const verifiedKeysMap = await this.icureStorage.loadSelfVerifiedKeys(dowt.dataOwner.id!)
      const allPublicKeys = new Set([
        ...this.dataOwnerApi.getHexPublicKeysWithSha1Of(dowt.dataOwner),
        ...this.dataOwnerApi.getHexPublicKeysWithSha256Of(dowt.dataOwner),
      ])
      const unavailableKeys = Array.from(allPublicKeys).flatMap((key) => {
        return availableKeysFpSet.has(fingerprintV1(key)) ? [] : [key]
      })
      const unknownKeys = Array.from(allPublicKeys).filter(
        (x) => !(fingerprintV1(x) in verifiedKeysMap) && !(availableKeys?.[fingerprintV1(x)]?.isDevice === true)
      )
      keysData.push({ dowt, availableKeys, unavailableKeys, unknownKeys })
    }
    const recoveryInfo = keysData.map(({ dowt, unavailableKeys, unknownKeys }) => ({ dataOwner: dowt, unavailableKeys, unknownKeys }))
    const recoveryAndVerificationResult = recoveryInfo.some((x) => x.unavailableKeys.length > 0 || x.unknownKeys.length > 0)
      ? await keyRecovererAndVerifier(recoveryInfo)
      : await nothingKeyRecovererAndVerifier(recoveryInfo)
    const keysCache: { [dataOwnerId: string]: { [fp: string]: KeyPairData } } = {}
    for (let i = 0; i < keysData.length; i++) {
      const keyData = keysData[i]
      const currAuthenticity = this.ensureFingerprintKeys(recoveryAndVerificationResult[keyData.dowt.dataOwner.id!].keyAuthenticity)
      const currExternallyRecovered = this.ensureFingerprintKeys(recoveryAndVerificationResult[keyData.dowt.dataOwner.id!].recoveredKeys)
      for (const [fp, keyPair] of Object.entries(currExternallyRecovered)) {
        const jwkPair = await this.primitives.RSA.exportKeys(keyPair, 'jwk', 'jwk')
        await this.icureStorage.saveKey(keyData.dowt.dataOwner.id!, fp, jwkPair, currAuthenticity[fp] != undefined ? currAuthenticity[fp] : true)
      }
      const updatedVerifiedMap = await this.icureStorage.saveSelfVerifiedKeys(
        keyData.dowt.dataOwner.id!,
        [...Object.keys(currAuthenticity), ...Object.keys(currExternallyRecovered)].reduce(
          (acc, currFp) => ({
            ...acc,
            [currFp]: currAuthenticity[currFp] != undefined ? currAuthenticity[currFp] : currFp in currExternallyRecovered ? true : undefined,
          }),
          {}
        )
      )
      const keysWithExternallyRecovered = {
        ...keyData.availableKeys,
        ...Object.fromEntries(Object.entries(currExternallyRecovered).map(([k, v]) => [k, { pair: v, isDevice: false }])),
      }
      let parentKeysForRecovery: { [p: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } } = {}
      for (let j = 0; j < i; j++) {
        parentKeysForRecovery = {
          ...keysCache[keysData[j].dowt.dataOwner.id!],
          ...parentKeysForRecovery,
        }
      }
      const additionallyRecovered = await this.recoverAndCacheKeys(
        keyData.dowt,
        this.plainKeysByFingerprint({
          ...keysWithExternallyRecovered,
          ...parentKeysForRecovery,
        })
      )
      const keys = {
        ...keysWithExternallyRecovered,
        ...Object.fromEntries(Object.entries(additionallyRecovered).map(([k, v]) => [k, { pair: v, isDevice: false }])),
      }
      keysCache[keyData.dowt.dataOwner.id!] = this.verifyKeys(keys, updatedVerifiedMap)
    }
    if (Object.entries(keysCache).some(([ownerId, data]) => ownerId !== self.dataOwner.id && !this.hasVerifiedKey(data))) {
      throw new Error('Some parent hcps have no verified keys: impossible to generate locally a new key for a parent.')
    } else if (this.hasVerifiedKey(keysCache[self.dataOwner.id!])) {
      this.keysCache = keysCache
      this.selfLegacyPublicKey = self.dataOwner.publicKey
      return undefined
    } else {
      const whatToDo = await currentOwnerKeyGenerator(self)
      if (whatToDo === false) {
        throw new Error(`No verified key found for ${self.dataOwner.id} and settings do not allow creation of a new key.`)
      } else if (typeof whatToDo == 'string' && whatToDo == 'keyless') {
        /*
         * Currently non-anonymous data owners aren't allowed to use keyless mode.
         * Today the delegator is not emitted in views and is not used for access control: an entity without delegation
         * self->self but only a delegation self->other would not be searchable by nor accessible to an explicit data
         * owner.
         * This is not a problem for anonymous data owner, since search and access control use the secure delegation
         * key.
         * Since keyless api was designed for use by patients this is not an issue at the time, but if in future we need
         * to support keyless api for explicit data owner before updating views we can add stubs to legacy delegations.
         */
        if (!this.selfIsAnonymous) throw new Error('Keyless api mode is available only for anonymous data owners.')
        this.keysCache = keysCache
        return undefined
      } else {
        const updateInfo = await this.createAndSaveNewKeyPair(whatToDo === true ? undefined : whatToDo, self)
        // self may be outdated now
        this.selfLegacyPublicKey = updateInfo.updatedSelf.stub.publicKey
        this.keysCache = {
          ...keysCache,
          [self.dataOwner.id!]: {
            ...keysCache[self.dataOwner.id!],
            [updateInfo.publicKeyFingerprint]: { pair: updateInfo.keyPair, isDevice: true, isVerified: true },
          },
        }
        return { pair: updateInfo.keyPair, fingerprint: updateInfo.publicKeyFingerprint }
      }
    }
  }

  private async createAndSaveNewKeyPair(
    importedKeyPair: undefined | KeyPair<CryptoKey>,
    selfDataOwner: DataOwnerWithType
  ): Promise<{ publicKeyFingerprint: string; keyPair: KeyPair<CryptoKey>; updatedSelf: CryptoActorStubWithType }> {
    const keyPair = importedKeyPair ?? (await this.primitives.RSA.generateKeyPair(ShaVersion.Sha256))
    const publicKeyHex = ua2hex(await this.primitives.RSA.exportKey(keyPair.publicKey, 'spki'))
    const jwKey = await this.primitives.RSA.exportKey(keyPair.publicKey, 'jwk')
    if (jwKey.alg !== 'RSA-OAEP-256') {
      throw new Error('Only keys generated with SHA-256 are currently supported')
    }
    const publicKeyFingerprint = fingerprintV1(publicKeyHex)
    await this.icureStorage.saveKey(
      selfDataOwner.dataOwner.id!,
      publicKeyFingerprint,
      await this.primitives.RSA.exportKeys(keyPair, 'jwk', 'jwk'),
      true
    )
    await this.icureStorage.saveSelfVerifiedKeys(selfDataOwner.dataOwner.id!, { [publicKeyFingerprint]: true })
    const updatedSelf = await this.dataOwnerApi.modifyCryptoActorStub({
      stub: {
        ...CryptoActorStub.fromDataOwner(selfDataOwner.dataOwner),
        publicKeysForOaepWithSha256: [...(selfDataOwner.dataOwner.publicKeysForOaepWithSha256 ?? []), publicKeyHex],
      },
      type: selfDataOwner.type,
    })
    return { publicKeyFingerprint, keyPair: keyPair, updatedSelf }
  }

  private async loadStoredKeys(
    dataOwner: DataOwnerWithType,
    pubKeysFingerprints: { [key: string]: string[] }
  ): Promise<{ [pubKeyFingerprint: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } }> {
    const pubKeysFingerprintsWithVersion = Object.entries(pubKeysFingerprints).flatMap(([shaVersion, fps]) => {
      return fps.map((fp) => [shaVersion, fp] as [ShaVersion, string])
    })
    return await pubKeysFingerprintsWithVersion.reduce(async (acc, [shaVersion, currentFingerprint]) => {
      const awaitedAcc = await acc
      let loadedPair: { pair: KeyPair<CryptoKey>; isDevice: boolean } | undefined = undefined
      try {
        const storedKeypair = await this.icureStorage.loadKey(dataOwner.dataOwner.id!, currentFingerprint, dataOwner.dataOwner.publicKey)
        if (!!storedKeypair) {
          const importedKey = await this.primitives.RSA.importKeyPair(
            'jwk',
            storedKeypair.pair.privateKey,
            'jwk',
            storedKeypair.pair.publicKey,
            shaVersion
          )
          loadedPair = { pair: importedKey, isDevice: storedKeypair.isDevice }
        }
      } catch (e) {
        console.warn('Error while loading keypair', currentFingerprint, e)
      }
      return loadedPair
        ? {
            ...awaitedAcc,
            [currentFingerprint]: loadedPair,
          }
        : awaitedAcc
    }, Promise.resolve({} as { [pubKeyFingerprint: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } }))
  }

  private verifyKeys(
    keys: { [pubKeyFingerprint: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } },
    verifiedKeysMap: { [pubKeyFingerprint: string]: boolean }
  ): { [pubKeyFingerprint: string]: KeyPairData } {
    return Object.fromEntries(
      Object.entries(keys).map(
        ([fp, keyData]) => [fp, { ...keyData, isVerified: keyData.isDevice || verifiedKeysMap?.[fp] === true }] as [string, KeyPairData]
      )
    )
  }

  private plainKeysByFingerprint(richKeys: { [pubKeyFingerprint: string]: { pair: KeyPair<CryptoKey> } }): {
    [pubKeyFingerprint: string]: KeyPair<CryptoKey>
  } {
    return Object.fromEntries(Object.entries(richKeys).map(([fp, keyData]) => [fp, keyData.pair]))
  }

  private ensureInitialised() {
    if (!this.keysCache) throw new Error('Key manager was not properly initialised')
  }

  private async recoverAndCacheKeys(
    dataOwner: DataOwnerWithType,
    availableKeys: { [pubKeyFingerprint: string]: KeyPair<CryptoKey> }
  ): Promise<{ [p: string]: KeyPair<CryptoKey> }> {
    const recoveredKeys = await this.keyRecovery.recoverKeys(dataOwner, availableKeys)
    for (const [fp, pair] of Object.entries(recoveredKeys)) {
      await this.icureStorage.saveKey(dataOwner.dataOwner.id!, fp, await this.primitives.RSA.exportKeys(pair, 'jwk', 'jwk'), false)
    }
    return recoveredKeys
  }

  private async loadAndRecoverKeysFor(
    dataOwner: DataOwnerWithType,
    ancestorsKeys: { [keyFp: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } }
  ): Promise<{ [keyFp: string]: { pair: KeyPair<CryptoKey>; isDevice: boolean } }> {
    const pubKeysFingerprints = {
      'sha-1': [...new Set(this.dataOwnerApi.getHexPublicKeysWithSha1Of(dataOwner.dataOwner))].map((x) => fingerprintV1(x)),
      'sha-256': [...new Set(this.dataOwnerApi.getHexPublicKeysWithSha256Of(dataOwner.dataOwner))].map((x) => fingerprintV1(x)),
    }
    const loadedKeys =
      pubKeysFingerprints['sha-1'].length + pubKeysFingerprints['sha-256'].length > 0 ? await this.loadStoredKeys(dataOwner, pubKeysFingerprints) : {}
    const loadedKeysFingerprints = Object.keys(loadedKeys)
    if (
      loadedKeysFingerprints.length !== pubKeysFingerprints['sha-1'].length + pubKeysFingerprints['sha-256'].length &&
      (loadedKeysFingerprints.length > 0 || Object.keys(ancestorsKeys).length > 0)
    ) {
      const recoveredKeys = await this.recoverAndCacheKeys(dataOwner, this.plainKeysByFingerprint({ ...loadedKeys, ...ancestorsKeys }))
      for (const [fp, pair] of Object.entries(recoveredKeys)) {
        loadedKeys[fp] = { pair, isDevice: false }
      }
    }
    return loadedKeys
  }

  private ensureFingerprintKeys<T>(obj: { [shouldBeFingerprint: string]: T }): { [definitelyFingerprint: string]: T } {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [fingerprintV1(k), v]))
  }

  private hasVerifiedKey(keysData: { [fp: string]: KeyPairData }) {
    return Object.values(keysData).some((x) => x.isVerified || x.isDevice)
  }
}
